import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Pagination,
  Avatar,
  Dropdown,
  Menu,
  Modal,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useUsers, User } from "../../hooks/useUsers";

interface UserTableProps {
  filters: Record<string, any>;
  onModifyUser: (user: User) => void;
  onDeleteUser: (userId: number) => void;
  canModifyUsers: boolean; // New prop for modify permission
  canDeleteUsers: boolean; // New prop for delete permission
}

const UserTable: React.FC<UserTableProps> = ({
  filters,
  onModifyUser,
  onDeleteUser,
  canModifyUsers,
  canDeleteUsers,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Process filters before passing to useUsers
  const processedFilters = { ...filters };

  // Special handling for user_type_id to convert to number
  if (processedFilters.user_type_id !== undefined) {
    processedFilters.user_type_id = Number(processedFilters.user_type_id);
    console.log(`Using user_type_id filter: ${processedFilters.user_type_id}`);
  } else if (Object.keys(processedFilters).length === 0) {
    // If no filters set, default to staff users
    processedFilters.user_type_id = 2;
  }

  // Make sure include_guests is properly typed as boolean
  if (processedFilters.include_guests !== undefined) {
    processedFilters.include_guests =
      processedFilters.include_guests === true ||
      processedFilters.include_guests === "true";
  } else {
    // If not set, default to false (don't show guests)
    processedFilters.include_guests = false;
  }

  // Log filters for debugging
  console.log("Current filters for API call:", processedFilters);

  const { data: usersData, isLoading: usersLoading } = useUsers(
    currentPage,
    processedFilters
  );

  // Handle delete confirmation
  const handleDelete = (userId: number) => {
    if (!canDeleteUsers) {
      message.error("You don't have permission to delete users");
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to delete this user?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => onDeleteUser(userId),
    });
  };

  // Handle modify user
  const handleModify = (user: User) => {
    if (!canModifyUsers) {
      message.error("You don't have permission to modify users");
      return;
    }

    onModifyUser(user);
  };

  // Dynamically build menu items based on permissions
  const getMenuItems = (record: User) => {
    const items = [];

    if (canModifyUsers) {
      items.push(
        <Menu.Item
          key="modify"
          icon={<EditOutlined />}
          onClick={() => handleModify(record)}
        >
          Modify
        </Menu.Item>
      );
    }

    if (canDeleteUsers) {
      items.push(
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
          danger
        >
          Delete
        </Menu.Item>
      );
    }

    return items;
  };

  const columns: ColumnsType<User> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      render: (name, record) => (
        <div className="flex items-center space-x-2">
          <Avatar
            icon={<UserOutlined />}
            src={record.avatar || record.user_type?.icon || undefined}
          />
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      ellipsis: true,
    },
    {
      title: "WhatsApp",
      dataIndex: "whatsapp_phone",
      key: "whatsapp_phone",
      ellipsis: true,
      render: (whatsapp) => whatsapp || "N/A",
    },
    {
      title: "DOB",
      dataIndex: "date_of_birth",
      key: "date_of_birth",
      ellipsis: true,
      render: (dob) => dob || "N/A",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      ellipsis: true,
      render: (gender) => gender || "N/A",
    },
    {
      title: "User Type",
      dataIndex: "user_type",
      key: "user_type",
      ellipsis: true,
      render: (user_type) => user_type?.name || "Unknown",
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
      ellipsis: true,
      render: (roles) => roles?.name || "N/A",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      ellipsis: true,
      render: (location) => location?.name || "Unknown",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (address) => address || "N/A",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      ellipsis: true,
      render: (state) => state || "N/A",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      ellipsis: true,
      render: (country) => country || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === 1 ? "green" : "red"}>
          {status === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
  ];

  // Only add Action column if user has either modify or delete permission
  if (canModifyUsers || canDeleteUsers) {
    columns.push({
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => {
        const menuItems = getMenuItems(record);

        // If no permissions, don't show dropdown
        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown overlay={<Menu>{menuItems}</Menu>} trigger={["click"]}>
            <button className="text-blue-500 hover:text-blue-700">
              <EllipsisOutlined />
            </button>
          </Dropdown>
        );
      },
    });
  }

  return (
    <div>
      <Table
        dataSource={usersData?.data ?? []}
        columns={columns}
        pagination={false}
        loading={usersLoading}
        bordered
        scroll={{ x: "max-content" }}
        className="custom-table"
        rowKey="id"
      />

      {/* Custom Pagination */}
      <div className="flex justify-end mt-4">
        <Pagination
          current={usersData?.meta.current_page ?? 1}
          total={usersData?.meta.total ?? 0}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default UserTable;
